import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  useToast,
  Heading,
  Box,
  Flex,
  Icon
} from '@chakra-ui/react';
import { supabase } from '../lib/supabaseClient';
import { FiMusic, FiCheck, FiClock } from 'react-icons/fi';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SpotifyRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

interface SpotifyPendingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Modal for users without subscription
export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Priority Access to Player</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Flex align="center" justify="center" p={4}>
              <Icon as={FiMusic} boxSize={12} color="purple.500" />
            </Flex>
            <Text>
              The SyncdIt player feature is currently in a phased rollout. Subscribers get priority access!
            </Text>
            <Text fontWeight="medium">
              Upgrade to a Pro subscription to get:
            </Text>
            <VStack align="start" pl={4}>
              <Flex align="center">
                <Icon as={FiCheck} color="green.500" mr={2} />
                <Text>Priority access to the player feature</Text>
              </Flex>
              <Flex align="center">
                <Icon as={FiCheck} color="green.500" mr={2} />
                <Text>Access to premium templates</Text>
              </Flex>
              <Flex align="center">
                <Icon as={FiCheck} color="green.500" mr={2} />
                <Text>Enhanced music service integration</Text>
              </Flex>
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Later
          </Button>
          <Button colorScheme="purple" onClick={() => window.location.href = '/pricing'}>
            View Subscription Options
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Modal for subscribers to request Spotify access
export const SpotifyRequestModal: React.FC<SpotifyRequestModalProps> = ({ isOpen, onClose, userId }) => {
  const [spotifyEmail, setSpotifyEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [spotifyName, setSpotifyName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    spotifyEmail?: string;
    confirmEmail?: string;
    spotifyName?: string;
  }>({});
  const toast = useToast();

  const validateForm = () => {
    const newErrors: typeof errors = {};
    
    if (!spotifyEmail) {
      newErrors.spotifyEmail = 'Spotify email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(spotifyEmail)) {
      newErrors.spotifyEmail = 'Invalid email format';
    }
    
    if (!confirmEmail) {
      newErrors.confirmEmail = 'Please confirm your email';
    } else if (confirmEmail !== spotifyEmail) {
      newErrors.confirmEmail = 'Emails do not match';
    }
    
    if (!spotifyName) {
      newErrors.spotifyName = 'Your name on Spotify is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Update player_access entry
      const { error } = await supabase
        .from('player_access')
        .update({
          spotify_email: spotifyEmail,
          spotify_name: spotifyName
        })
        .eq('user_id', userId);
      
      if (error) throw error;
      
      toast({
        title: 'Request submitted',
        description: "Thank you! We've received your request for Spotify access. You'll be notified when access is granted.",
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      onClose();
    } catch (error) {
      console.error('Error submitting Spotify access request:', error);
      toast({
        title: 'Error',
        description: 'There was an error submitting your request. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Request Spotify Access</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text>
              To provide you with access to the Spotify integration in our player, we need to verify your Spotify account.
            </Text>
            
            <FormControl isInvalid={!!errors.spotifyEmail}>
              <FormLabel>Email Address Tied to Your Spotify Account</FormLabel>
              <Input 
                value={spotifyEmail}
                onChange={(e) => setSpotifyEmail(e.target.value)}
                placeholder="your.email@example.com"
              />
              {errors.spotifyEmail && <FormErrorMessage>{errors.spotifyEmail}</FormErrorMessage>}
            </FormControl>
            
            <FormControl isInvalid={!!errors.confirmEmail}>
              <FormLabel>Confirm Spotify Email</FormLabel>
              <Input 
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder="Confirm your email"
              />
              {errors.confirmEmail && <FormErrorMessage>{errors.confirmEmail}</FormErrorMessage>}
            </FormControl>
            
            <FormControl isInvalid={!!errors.spotifyName}>
              <FormLabel>Your Name on Spotify</FormLabel>
              <Input 
                value={spotifyName}
                onChange={(e) => setSpotifyName(e.target.value)}
                placeholder="Your display name on Spotify"
              />
              {errors.spotifyName && <FormErrorMessage>{errors.spotifyName}</FormErrorMessage>}
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button 
            colorScheme="green" 
            onClick={handleSubmit}
            isLoading={isSubmitting}
          >
            Submit Request
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Modal for subscribers who have submitted their Spotify info but don't have access yet
export const SpotifyPendingModal: React.FC<SpotifyPendingModalProps> = ({ isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Access Request Pending</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Flex align="center" justify="center" p={4}>
              <Icon as={FiClock} boxSize={12} color="blue.500" />
            </Flex>
            <Text>
              We've received your request for access to the Spotify integration in our player.
            </Text>
            <Text>
              Our team is currently processing your request. You'll receive an email notification when you've been granted access.
            </Text>
            <Text fontWeight="medium">
              Thank you for your patience!
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" onClick={onClose}>
            OK
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}; 